import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { staffService } from '../../services/apiService';

const StaffManagement = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRole, filterDepartment]);

  const fetchData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('🔐 Current user:', user);
      console.log('🔐 Current user role:', user.role);
      
      if (user.role !== 'SUPER_ADMIN') {
        toast.error(`Access denied: You need SUPER_ADMIN role. Your current role: ${user.role || 'Unknown'}`);
        navigate('/dashboard');
        return;
      }

      const [staffData, statsData] = await Promise.all([
        staffService.getAllStaff({ role: filterRole, department: filterDepartment }),
        staffService.getStatistics()
      ]);
      setStaff(staffData.staff);
      setStatistics(statsData.statistics);
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 403) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        toast.error(`Access denied: ${error.response?.data?.message}. Please login as SUPER_ADMIN. Current role: ${user.role}`);
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        toast.error('Failed to fetch staff data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await staffService.updateStaff(id, { isActive: !currentStatus });
      toast.success(`Staff member ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update staff status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this staff member?')) {
      try {
        await staffService.deleteStaff(id);
        toast.success('Staff member deactivated successfully');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete staff member');
      }
    }
  };

  const roleLabels = {
    'LIBRARY_STAFF': 'Library',
    'HOD': 'HOD',
    'ACCOUNTS_SECTION': 'Accounts Section',
    'EXAM_SECTION': 'Exam Section',
    'ADMIN': 'Admin',
    'FACULTY': 'Faculty',
    'STAFF': 'Staff'
  };

  // Filter and search staff
  const filteredStaff = staff.filter(member => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      member.name?.toLowerCase().includes(searchLower) ||
      member.collegeEmail?.toLowerCase().includes(searchLower) ||
      member.phoneNumber?.includes(searchQuery) ||
      member.staffId?.toLowerCase().includes(searchLower) ||
      member.department?.toLowerCase().includes(searchLower);

    // Role filter
    const matchesRole = !filterRole || member.role === filterRole;

    // Department filter
    const matchesDepartment = !filterDepartment || member.department === filterDepartment;

    // Status filter
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && member.isActive) ||
      (filterStatus === 'inactive' && !member.isActive);

    return matchesSearch && matchesRole && matchesDepartment && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Staff Management</h1>
              <p className="text-slate-600 mt-2">Manage all staff accounts and permissions</p>
            </div>
            <Link
              to="/admin/staff/create"
              className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors inline-flex items-center gap-2"
            >
              <span className="text-xl">+</span>
              Add Staff Member
            </Link>
          </div>

          {/* Statistics */}
          {statistics && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <p className="text-sm text-slate-600 mb-1">Total Staff</p>
                <p className="text-3xl font-bold text-slate-900">{statistics.total}</p>
              </div>
              <div className="bg-green-50 rounded-lg shadow-sm p-6">
                <p className="text-sm text-green-600 mb-1">Active</p>
                <p className="text-3xl font-bold text-green-700">{statistics.active}</p>
              </div>
              <div className="bg-red-50 rounded-lg shadow-sm p-6">
                <p className="text-sm text-red-600 mb-1">Inactive</p>
                <p className="text-3xl font-bold text-red-700">{statistics.inactive}</p>
              </div>
              <div className="bg-blue-50 rounded-lg shadow-sm p-6">
                <p className="text-sm text-blue-600 mb-1">Roles</p>
                <p className="text-3xl font-bold text-blue-700">
                  {Object.keys(statistics.byRole || {}).length}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Search Staff</label>
            <input
              type="text"
              placeholder="Search by name, email, phone, employee ID, or department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-2">Filter by Role</label>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">All Roles</option>
                {Object.entries(roleLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-2">Filter by Department</label>
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">All Departments</option>
                <option value="CS">CS</option>
                <option value="CD">CD</option>
                <option value="CI">CI</option>
                <option value="CY">CY</option>
                <option value="EC">EC</option>
                <option value="ME">ME</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-2">Filter by Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          {(searchQuery || filterRole || filterDepartment || filterStatus) && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Showing {filteredStaff.length} of {staff.length} staff members
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterRole('');
                  setFilterDepartment('');
                  setFilterStatus('');
                }}
                className="text-sm text-primary hover:text-primary-dark font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Staff List */}
        {filteredStaff.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Staff Members Found</h3>
            <p className="text-slate-600 mb-6">
              {searchQuery || filterRole || filterDepartment || filterStatus ? 'No staff match your search or filters' : 'Start by adding a new staff member'}
            </p>
            <Link
              to="/admin/staff/create"
              className="inline-block px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
            >
              Add First Staff Member
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Employee ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredStaff.map((member, index) => (
                    <tr key={member.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-slate-600">{index + 1}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">{member.name}</div>
                        {member.phoneNumber && (
                          <div className="text-sm text-slate-500">{member.phoneNumber}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-semibold text-blue-600">{member.staffId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-900">{member.collegeEmail}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {roleLabels[member.role]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {member.department || 'GLOBAL'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          member.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {member.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <Link
                            to={`/admin/staff/${member.id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleToggleActive(member.id, member.isActive)}
                            className="text-orange-600 hover:text-orange-900"
                          >
                            {member.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDelete(member.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffManagement;
